import { useCallback, useRef, useState } from 'react';
import {
  CheckCircle, CloudCheck, CloudSlash, Plus, Trash, UploadSimple,
  Camera, X, Sparkle, FileText, CircleNotch, Lightbulb,
} from '@phosphor-icons/react';

/**
 * Journey chrome and container primitives.
 */

export function StepShell({ eyebrow, title, lede, why, encouragement, children }) {
  return (
    <section className="ob-card">
      {eyebrow && (
        <span className="ob-eyebrow">
          <Sparkle size={12} weight="fill" />
          {eyebrow}
        </span>
      )}
      <h1 className="ob-h1">{title}</h1>
      {lede && <p className="ob-lede">{lede}</p>}

      {why && (
        <details className="ob-why">
          <summary>Why we ask this</summary>
          <p>{why}</p>
        </details>
      )}

      <div className="ob-body">{children}</div>

      {encouragement && (
        <p className="ob-encourage">
          <Lightbulb size={17} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{encouragement}</span>
        </p>
      )}
    </section>
  );
}

export function SavedIndicator({ state, error }) {
  const content = {
    saving: { icon: <CircleNotch size={14} weight="bold" className="ob-spin" />, text: 'Saving' },
    saved: { icon: <CloudCheck size={15} weight="fill" />, text: 'Saved' },
    error: { icon: <CloudSlash size={15} weight="fill" />, text: 'Not saved' },
    idle: { icon: null, text: '' },
  }[state] || { icon: null, text: '' };

  if (!content.text) return null;

  return (
    <span className="ob-saved" data-state={state} title={state === 'error' ? error : undefined}>
      {content.icon}
      {content.text}
    </span>
  );
}

export function ProgressBar({ percent, label }) {
  return (
    <div className="ob-progress-wrap">
      <div className="ob-progress-meta">
        <span className="ob-progress-label">{label}</span>
        <span className="ob-progress-value">{percent}% complete</span>
      </div>
      <div
        className="ob-progress-track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Profile completion"
      >
        <div className="ob-progress-fill" style={{ width: `${percent}%` }} data-complete={percent >= 100} />
      </div>
    </div>
  );
}

/**
 * The side rail. Its job is to make the profile feel like it is being built:
 * sections tick over as they are finished, and any section is jumpable.
 */
export function ProfileRail({ steps, currentKey, sections, onJump }) {
  return (
    <aside className="ob-rail" aria-label="Your profile steps">
      <details className="ob-rail-card" open>
        <summary className="ob-rail-summary">
          <span>
            <p className="ob-rail-title">Your profile</p>
            <p className="ob-rail-sub">Tap any step to jump — even if not filled yet</p>
          </span>
          <span className="ob-rail-chevron" aria-hidden="true" />
        </summary>
        <div className="ob-rail-list">
          {steps.map((step) => {
            const state = sections[step.key]?.skipped
              ? 'skipped'
              : sections[step.key]?.is_complete
                ? 'done'
                : step.key === currentKey
                  ? 'active'
                  : 'todo';
            return (
              <button
                key={step.key}
                type="button"
                className="ob-rail-item"
                data-state={state}
                onClick={() => onJump(step.key)}
              >
                <span className="ob-rail-dot">
                  {state === 'done' && (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6.2L4.8 9L10 3.4" stroke="currentColor" strokeWidth="2.4"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="ob-rail-label">{step.label}</span>
              </button>
            );
          })}
        </div>
      </details>
    </aside>
  );
}

export function MilestoneToast({ message }) {
  if (!message) return null;
  return (
    <div className="ob-milestone" role="status">
      <CheckCircle size={17} weight="fill" />
      {message}
    </div>
  );
}

/** Progressive disclosure for repeatable entries. */
export function Repeatable({ entries, renderEntry, onAdd, addLabel, emptyLabel, titleFor, minEntries = 0 }) {
  return (
    <div className="ob-repeat">
      {entries.length === 0 && emptyLabel && <div className="ob-empty">{emptyLabel}</div>}

      {entries.map((entry, index) => (
        <div className="ob-repeat-card" key={entry._id ?? index}>
          <div className="ob-repeat-head">
            <span className="ob-repeat-title">{titleFor(entry, index)}</span>
            {entries.length > minEntries && (
              <button
                type="button"
                className="ob-icon-btn"
                aria-label="Remove this entry"
                onClick={() => entry._onRemove?.()}
              >
                <Trash size={16} />
              </button>
            )}
          </div>
          <div className="ob-repeat-body">{renderEntry(entry, index)}</div>
        </div>
      ))}

      <button type="button" className="ob-add-btn" onClick={onAdd}>
        <Plus size={16} weight="bold" />
        {addLabel}
      </button>
    </div>
  );
}

/** Drag-and-drop or click to upload. */
export function FileDrop({ onFile, accept, title, hint, busy }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    if (files && files[0]) onFile(files[0]);
  }, [onFile]);

  return (
    <>
      <div
        className="ob-drop"
        data-over={over}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="ob-drop-icon">
          {busy
            ? <CircleNotch size={26} className="ob-spin" />
            : <UploadSimple size={26} weight="duotone" />}
        </div>
        <div className="ob-drop-title">{busy ? 'Uploading…' : title}</div>
        <div className="ob-drop-hint">{hint}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="ob-sr-only"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </>
  );
}

export function FileRow({ name, size, onRemove }) {
  const kb = size ? Math.round(size / 1024) : null;
  return (
    <div className="ob-file-row">
      <FileText size={19} weight="duotone" style={{ color: '#4f46e5', flexShrink: 0 }} />
      <span className="ob-file-name">{name}</span>
      {kb && <span className="ob-file-size">{kb} KB</span>}
      <button type="button" className="ob-icon-btn" aria-label={`Remove ${name}`} onClick={onRemove}>
        <X size={15} weight="bold" />
      </button>
    </div>
  );
}

export function AvatarUpload({ url, onFile, onRemove, busy }) {
  const inputRef = useRef(null);
  const base = url && url.startsWith('/') ? url : url;

  return (
    <div className="ob-avatar-wrap">
      {base ? (
        <img src={base} alt="Your profile photo" className="ob-avatar" />
      ) : (
        <div
          className="ob-avatar-empty"
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          {busy
            ? <CircleNotch size={22} className="ob-spin" />
            : <Camera size={26} weight="duotone" />}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Profile photo</div>
          <div className="ob-hint">Optional. A clear headshot helps your mentors recognise you.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="ob-btn ob-btn-ghost" style={{ minHeight: 38, padding: '8px 14px', fontSize: 13 }}
            onClick={() => inputRef.current?.click()}>
            {base ? 'Change' : 'Add photo'}
          </button>
          {base && (
            <button type="button" className="ob-btn ob-btn-text" style={{ minHeight: 38, padding: '8px 10px', fontSize: 13 }}
              onClick={onRemove}>
              Remove
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="ob-sr-only"
        onChange={(e) => {
          if (e.target.files?.[0]) onFile(e.target.files[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

export function SectionGroup({ label, children }) {
  return (
    <div className="ob-divider">
      <div className="ob-group-label">{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>{children}</div>
    </div>
  );
}
